import { useEffect, useRef } from 'react';
import classnames from 'classnames';

import { getErrorMessage } from '@common/utils/errorMessage';
import { handleError } from '@common/utils/handleError';
import { DatoEmbedRecord } from '@interfaces';

import styles from './youtube-embed.module.scss';

type YouTubeEmbedData = {
	author_name: string;
	author_url: string;
	height: number;
	html: string;
	provider_name: string;
	provider_url: string;
	thumbnail_height: number;
	thumbnail_url: string;
	thumbnail_width: number;
	title: string;
	type: string;
	version: string;
	width: number;
};

type YouTubeEmbedProps = {
	className?: string;
	embed: DatoEmbedRecord;
};

export const YouTubeEmbed = ({
	className,
	embed: { url },
}: YouTubeEmbedProps) => {
	const embedRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		fetch(`https://www.youtube.com/oembed?url=${url}`)
			.then((response) => response.json())
			.then((data: YouTubeEmbedData) => {
				// Convert the HTML into a document fragment so we can make some changes to it
				const documentFragment = document
					.createRange()
					.createContextualFragment(data.html);

				const iframeElement = documentFragment.querySelector('iframe');

				if (iframeElement) {
					// Update iframe src and attributes
					const srcUrl = new URL(iframeElement.src);

					srcUrl.searchParams.append('playsinline', '');

					iframeElement.setAttribute('loading', 'lazy');
					iframeElement.setAttribute('src', srcUrl.toString());
				}

				if (embedRef.current) {
					embedRef.current.style.setProperty(
						'--youtube-embed-aspect-ratio',
						`${data.width} / ${data.height}`,
					);

					embedRef.current.replaceChildren(documentFragment);
				}
			})
			.catch((error) => {
				handleError(getErrorMessage(error));
			});
	}, [url, embedRef]);

	return <div className={classnames(className, styles.embed)} ref={embedRef} />;
};
